/**
 * Le grand procès rejouable (O.J. Simpson…), dans la salle de son époque :
 * la Superior Court de Los Angeles. Ouvre une salle et y installe l'avocat.
 */
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { usePublicAuth } from '@/hooks/usePublicAuth';
import { getDateLocale } from '@/i18n';
import { getIdToken } from '@/lib/firebase';

import US_PLATE from './art/plates/court_us.webp'; // Superior Court (art/courts/build_courts.py)
import { withLang } from './trialTypes';


interface FamousCase {
  slug: string;
  title: string;
  headline: string;
  date: string;
  label: string;
  disclaimer: string;
}

export function FamousTrial() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loginWithGoogle } = usePublicAuth();
  const [cases, setCases] = useState<FamousCase[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(withLang('/api/trials/famous'))
      .then(r => (r.ok ? r.json() : { cases: [] }))
      .then(d => setCases(d.cases ?? []))
      .catch(() => setCases([]));
  }, []);

  const c = cases[0];
  if (!c) return null;
  const date = new Date(c.date).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      if (!user) await loginWithGoogle();
      const token = await getIdToken();
      if (!token) throw new Error(t('tribunal.famous.login_required'));
      const r = await fetch(withLang(`/api/trials/famous/${c.slug}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(t('tribunal.modal.errors.generic'));
      const trial = await r.json();
      navigate(`/tribunal/${trial.id}?join=1`);
    } catch (e) {
      setError((e as Error)?.message || t('tribunal.modal.errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5 }}
      className="max-w-6xl mx-auto px-4 mt-14 lg:mt-16 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_600px] gap-6 lg:gap-10 items-center"
    >
      <div className="order-2 lg:order-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-[10px] font-bold uppercase tracking-[.14em] px-2.5 py-1 rounded-md bg-[#d9a441] text-[#2a1d12]">{t('tribunal.famous.badge')}</span>
          <span className="text-xs text-[#6e5f4b]">{t('tribunal.carousel.verdict_on', { date })}</span>
        </div>
        <h2 className="mt-3 font-['IM_Fell_English',Georgia,serif] font-normal text-[40px] sm:text-[56px] leading-none text-[#2a1d12] text-balance">{t('tribunal.famous.title')}</h2>
        <p className="mt-2 font-serif text-lg sm:text-xl text-[#2a1d12]">{c.title}</p>
        <p className="mt-3 text-[15px] sm:text-base leading-relaxed text-[#4a3a28] max-w-xl">{t('tribunal.famous.pitch', { date })}</p>
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            onClick={start}
            disabled={busy}
            className="h-12 px-6 rounded-xl bg-[#d9a441] hover:bg-[#e0b866] text-[#2a1d12] font-bold shadow-md shadow-[#a8742a]/20 disabled:opacity-60"
          >
            {busy ? t('tribunal.famous.opening') : t('tribunal.famous.cta')}
          </button>
          <span className="text-xs text-[#6e5f4b]">{t('tribunal.famous.cta_hint')}</span>
        </div>
        {error && <p role="alert" className="mt-3 text-sm font-semibold text-[#9b3a2e]">{error}</p>}
        <p className="mt-4 text-[11px] leading-relaxed text-[#6e5f4b]/90 max-w-xl">{c.disclaimer}</p>
      </div>
      <figure className="order-1 lg:order-2 m-0 min-w-0">
        <div className="p-1.5 sm:p-2 bg-[#fffdf8] border border-[#d9ccb2] shadow-[0_24px_50px_-20px_rgba(42,29,18,.45)]">
          <div className="relative overflow-hidden aspect-[16/9] lg:aspect-[600/340]">
            <img
              src={US_PLATE}
              alt=""
              className="absolute max-w-none w-[150%] left-[-22%] top-[-12%] sm:w-[125%] sm:left-[-12%] sm:top-[-6%]"
            />
          </div>
        </div>
        <figcaption className="mt-2 flex justify-between gap-4 font-['IM_Fell_English',Georgia,serif] text-[#3b2a1a]">
          <span className="tracking-[.08em] text-[13px] sm:text-[15px]">{t('tribunal.home.famous_caption')}</span>
          <span className="italic text-xs text-[#7a6650] shrink-0">{t('tribunal.home.famous_place')}</span>
        </figcaption>
      </figure>
    </motion.section>
  );
}
